/**
 * Zeniora Promo Engine - 核心逻辑引擎 (智能多推荐 & 比价版)
 * 
 * 职责：
 * 1. 监听全局 cart:updated 和 cart:refresh 事件。
 * 2. 计算当前购物车的促销决策状态（优先级决策树，基于 Collection 触发的 BOGO）。
 * 3. 使用 Shopify Recommendations API 动态抓取前 3 个配对关联产品，支持获取其完整变体。
 * 4. 协同 ZenioraPromoWidgets 将 UI 渲染并挂载到相应的 Liquid 占位符中。
 */

class ZenioraPromoEngine {
  constructor() {
    this.config = window.ZenioraPromoConfig || {};
    this.debug = this.config.generalSettings?.debug || false;
    this.cartData = null;
    this.activeRule = null;
    this.pairedProductsList = []; // 缓存动态拉取到的配对推荐商品列表 (最多3个)
    this.promoState = 'INITIALIZING';
    this.currentRequestId = 0;
    this.pdpRequestId = 0;
    
    // 缓存数据，降低网络开销
    this.productsCache = {};
    this.collectionsCache = window.ZenioraCartCollections || {}; // 初始购物车单品所属 Collection 映射
    this.collectionProductsCache = {}; // 缓存 collection 下的所有商品 handle 映射
    
    this.init();
  }

  getCache(key) {
    try {
      const val = sessionStorage.getItem('zeniora_promo_cache_' + key);
      return val ? JSON.parse(val) : null;
    } catch (e) {
      return null;
    }
  }

  setCache(key, value) {
    try {
      sessionStorage.setItem('zeniora_promo_cache_' + key, JSON.stringify(value));
    } catch (e) {}
  }

  log(...args) {
    if (this.debug) {
      console.log('%c[Zeniora Promo Engine]', 'color: #7c3aed; font-weight: bold;', ...args);
    }
  }

  /**
   * 初始化引擎
   */
  init() {
    this.log('Initializing Smart Mix & Match Engine...');
    
    // 1. 全局购物车更新事件监听（针对 Minimog v5.9 Native cart.js 的数量变动事件）
    document.addEventListener('cart:updated', (event) => {
      this.log('Cart updated event received!', event.detail.cart);
      this.analyzeCart(event.detail.cart);
    });

    // 2. 监听 cart:refresh 事件（用于在加购商品、打开抽屉等 DOM 发生网络替换后，延时拉取最新状态重绘 Widget）
    document.addEventListener('cart:refresh', () => {
      this.log('Cart refresh event received. Scheduling analyzeCart...');
      setTimeout(async () => {
        try {
          const response = await fetch('/cart.js');
          if (response.ok) {
            const cart = await response.json();
            await this.analyzeCart(cart);
          }
        } catch (err) {
          console.error('[Zeniora Promo Engine] Error syncing cart after refresh:', err);
        }
      }, 600);
    });

    // 3. 监听初始 DOM 加载，用于初次获取购物车状态与 PDP 逻辑
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.handlePageLoad());
    } else {
      this.handlePageLoad();
    }
  }

  /**
   * 页面初始加载处理
   */
  async handlePageLoad() {
    this.log('Page loaded, running initial checks...');
    
    // 1. 初次主动拉取购物车状态
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cart = await response.json();
        await this.analyzeCart(cart);
      }
    } catch (err) {
      console.error('[Zeniora Promo Engine] Error fetching initial cart:', err);
    }

    // 2. 检查并运行 PDP 联合加购 Widget
    await this.initPdpWidget();
  }

  /**
   * 判断某个商品是否在指定的 Collection 中
   */
  async isProductInCollection(productHandle, collectionHandle) {
    if (collectionHandle === 'all-products' || !collectionHandle) {
      return true;
    }

    if (this.collectionsCache[productHandle]) {
      return this.collectionsCache[productHandle].includes(collectionHandle);
    }

    const cacheKey = 'collection_' + collectionHandle;
    let collectionHandles = this.collectionProductsCache[collectionHandle] || this.getCache(cacheKey);

    if (!collectionHandles) {
      try {
        const res = await fetch(`/collections/${collectionHandle}/products.json?limit=250`);
        if (res.ok) {
          const data = await res.json();
          collectionHandles = data.products.map(p => p.handle);
          this.collectionProductsCache[collectionHandle] = collectionHandles;
          this.setCache(cacheKey, collectionHandles);
        } else {
          collectionHandles = [];
          this.collectionProductsCache[collectionHandle] = [];
        }
      } catch (err) {
        console.error('[Zeniora Promo Engine] Error matching collection products:', err);
        return false;
      }
    } else {
      this.collectionProductsCache[collectionHandle] = collectionHandles;
    }

    const isMatch = collectionHandles.includes(productHandle);
    
    if (!this.collectionsCache[productHandle]) this.collectionsCache[productHandle] = [];
    if (isMatch && !this.collectionsCache[productHandle].includes(collectionHandle)) {
      this.collectionsCache[productHandle].push(collectionHandle);
    }
    
    return isMatch;
  }

  /**
   * PDP 页面逻辑：按 Collection 触发活动，并动态使用 Recommendations API 抓取最多 3 个配对赠品供用户选择
   */
  async initPdpWidget() {
    const pdpPlaceholder = document.querySelector('.zeniora-promo-pdp-placeholder');
    if (!pdpPlaceholder) return;

    this.log('PDP placeholder detected. Evaluating Collection BOGO rules...');
    
    const productId = pdpPlaceholder.dataset.productId;
    const collectionsAttr = pdpPlaceholder.dataset.collections || '';
    const productCollections = collectionsAttr.split(',').map(c => c.trim());
    
    // 缓存当前 PDP 商品所属的 Collections
    const pathParts = window.location.pathname.split('/');
    const currentHandle = pathParts[pathParts.length - 1];
    if (currentHandle && productCollections.length > 0) {
      this.collectionsCache[currentHandle] = productCollections;
    }

    // 匹配 B2G1 规则
    const matchedRule = this.config.b2g1Rules?.find(rule => {
      if (!rule.enabled) return false;
      return rule.triggerCollectionHandle === 'all-products' || productCollections.includes(rule.triggerCollectionHandle);
    });

    if (!matchedRule) {
      this.log('No active BOGO rule matches current product collections:', productCollections);
      pdpPlaceholder.innerHTML = '';
      pdpPlaceholder.style.display = 'none';
      return;
    }

    this.log('B2G1 Rule Matched for PDP product. Sync rendering skeleton...');
    
    // 立即同步渲染骨架屏以消除 CLS 位移
    pdpPlaceholder.style.display = 'block';
    if (window.ZenioraPromoWidgets?.renderPdpSkeleton) {
      window.ZenioraPromoWidgets.renderPdpSkeleton(pdpPlaceholder);
    }

    const pdpReqId = ++this.pdpRequestId;

    try {
      // 1. 动态获取 2 个关联推荐商品 (intent=complementary)
      const recommendedProducts = await this.fetchSmartRecommendations(productId, 2);
      
      if (pdpReqId !== this.pdpRequestId) return;

      if (recommendedProducts.length < 2) {
        this.log('Not enough recommendations found for BOGO paired products. Required: 2, Found:', recommendedProducts.length);
        pdpPlaceholder.innerHTML = '';
        pdpPlaceholder.style.display = 'none';
        return;
      }

      // 2. 获取当前主商品详情
      const primaryProduct = await this.fetchProductDetails(currentHandle);

      if (pdpReqId !== this.pdpRequestId) return;

      if (primaryProduct && recommendedProducts.length > 0) {
        this.log('Loaded PDP BOGO Bundle components. Options count:', recommendedProducts.length);
        pdpPlaceholder.style.display = 'block';
        if (window.ZenioraPromoWidgets?.renderPdpWidget) {
          window.ZenioraPromoWidgets.renderPdpWidget(pdpPlaceholder, matchedRule, primaryProduct, recommendedProducts);
        }
      } else {
        pdpPlaceholder.innerHTML = '';
        pdpPlaceholder.style.display = 'none';
      }
    } catch (err) {
      console.error('[Zeniora Promo Engine] Error loading dynamic BOGO PDP Widget:', err);
      if (pdpReqId === this.pdpRequestId) {
        pdpPlaceholder.innerHTML = '';
        pdpPlaceholder.style.display = 'none';
      }
    }
  }

  /**
   * 核心决策树：分析购物车数据并计算当前活动状态 (支持多推荐与比价)
   */
  async analyzeCart(cart) {
    const requestId = ++this.currentRequestId;

    // 自动数据降级与自愈保护：如果传入的 cart 数据非完整对象（如 Minimog 在某些更新分发中仅传入单品而非 cart 根对象），直接主动拉取最新的 cart.js
    if (!cart || !cart.items) {
      this.log('Cart parameter is missing or incomplete. Fetching latest cart.js data from server...');
      try {
        const response = await fetch('/cart.js');
        if (response.ok) {
          cart = await response.json();
        } else {
          return;
        }
      } catch (err) {
        console.error('[Zeniora Promo Engine] Error fetching cart fallback details:', err);
        return;
      }
    }

    if (requestId !== this.currentRequestId) return;

    this.cartData = cart;
    this.log('Analyzing cart items...', cart.items);

    if (cart.items.length === 0) {
      if (requestId !== this.currentRequestId) return;
      this.promoState = 'NO_PROMO';
      this.activeRule = null;
      this.pairedProductsList = [];
      await this.renderPromoUI();
      return;
    }

    let incompleteB2g1Rule = null;
    let completeB2g1Rule = null;
    let matchedPairedList = [];

    // 1. 检查 B2G1 规则 (全站 Mix & Match，任意三件触发)
    if (this.config.b2g1Rules && this.config.b2g1Rules.length > 0) {
      for (const rule of this.config.b2g1Rules) {
        if (!rule.enabled) continue;

        // 寻找购物车中所有满足 B2G1 触发 Collection 条件的商品项并计算总数量
        const triggerItems = [];
        let totalTriggerQty = 0;
        
        for (const item of cart.items) {
          const isIn = await this.isProductInCollection(item.handle, rule.triggerCollectionHandle);
          if (isIn) {
            triggerItems.push(item);
            totalTriggerQty += item.quantity;
          }
        }

        if (totalTriggerQty > 0) {
          this.log(`[Zeniora B2G1] Found B2G1 trigger products in cart. Total quantity: ${totalTriggerQty}`);
          
          // 全站 Mix & Match 判定 (买二送一，即 3 件门槛)：
          // - 当数量为 1 件或 2 件时，B2G1 状态为未达成 (incomplete)，促使加购配件以解锁。
          // - 大于等于 3 件时，均视为完全达成。
          if (totalTriggerQty === 1 || totalTriggerQty === 2) {
            incompleteB2g1Rule = {
              ...rule,
              totalTriggerQty: totalTriggerQty // 挂载触发商品总数，以便 UI 层动态计算缺项数与精度
            };
            
            // 使用落单的第一件商品的 product_id 来拉取最多五个智能互补配件作为推荐
            const firstTriggerItem = triggerItems[0];
            const recs = await this.fetchSmartRecommendations(firstTriggerItem.product_id, 5);
            if (recs && recs.length > 0) {
              matchedPairedList = recs;
            }
          } else {
            completeB2g1Rule = rule;
            matchedPairedList = []; // 已经达成 3 件触发，不需要显示凑单推荐
          }
          break; // 匹配到一个规则即锁定
        }
      }
    }

    if (requestId !== this.currentRequestId) {
      this.log('Outdated analyzeCart request discarded. ID:', requestId, 'Current:', this.currentRequestId);
      return;
    }

    // 2. 运行优先级判定链 (互斥关系)
    if (incompleteB2g1Rule && matchedPairedList.length > 0) {
      // 第一优先级：B2G1 缺项提醒 (显示 B2G1 专属进度条与推荐配件)
      this.promoState = 'INCOMPLETE_B2G1';
      this.activeRule = incompleteB2g1Rule;
      this.pairedProductsList = matchedPairedList;
      this.log('Priority State: INCOMPLETE_B2G1. Displaying exclusive B2G1 progress bar.');
      
      await this.renderPromoUI();
    } else {
      // 第二/第三优先级：免邮进度条判定 (此时 BOGO 已达成或不存在，仅显示免邮专属进度条与常规凑单品)
      const shippingConfig = this.config.freeShipping;
      if (shippingConfig && shippingConfig.enabled) {
        const cartTotal = cart.total_price;
        const threshold = shippingConfig.threshold;

        if (cartTotal < threshold) {
          this.promoState = 'INCOMPLETE_SHIPPING';
          this.activeRule = null; // 专属免邮进度条
          this.pairedProductsList = [];
          this.log('Priority State: INCOMPLETE_SHIPPING. Total:', cartTotal, 'Threshold:', threshold);
        } else {
          this.promoState = 'ALL_SUCCESS';
          this.activeRule = null;
          this.pairedProductsList = [];
          this.log('Priority State: ALL_SUCCESS (Free shipping unlocked).');
        }
        
        await this.renderPromoUI();
      } else {
        this.promoState = 'NO_PROMO';
        this.activeRule = null;
        this.pairedProductsList = [];
        await this.renderPromoUI();
      }
    }
  }

  /**
   * 触发 UI 渲染逻辑
   */
  async renderPromoUI() {
    const goalPlaceholder = document.getElementById('zeniora-promo-drawer-goal');
    const upsellPlaceholder = document.getElementById('zeniora-promo-drawer-upsell');

    if (!window.ZenioraPromoWidgets) {
      console.warn('[Zeniora Promo Engine] ZenioraPromoWidgets is not loaded yet.');
      return;
    }

    // 1. 渲染进度条 Widget
    if (goalPlaceholder) {
      window.ZenioraPromoWidgets.renderProgressBar(goalPlaceholder, this.promoState, this.cartData, this.activeRule);
    }

    // 2. 渲染凑单 / 配对 Widget
    if (upsellPlaceholder) {
      let recommendations = [];
      
      if (this.promoState === 'INCOMPLETE_B2G1' && this.pairedProductsList.length > 0) {
        // 缺 BOGO 配对品，直接推荐拉取到的前三个推荐产品，让用户有多选空间
        recommendations = this.pairedProductsList;
      } else if (this.promoState === 'INCOMPLETE_SHIPPING' || this.promoState === 'ALL_SUCCESS' || this.promoState === 'NO_PROMO') {
        // 渲染满额免邮凑单品
        recommendations = await this.fetchShippingUpsells();
      }

      window.ZenioraPromoWidgets.renderCartUpsell(upsellPlaceholder, this.promoState, recommendations, this.cartData);
    }
  }

  /**
   * 获取动态推荐产品 (Shopify Recommendations API) 并获取其完整变体数据
   */
  async fetchSmartRecommendations(productId, limit = 2) {
    const cacheKey = `recs_${productId}_${limit}`;
    const cached = this.getCache(cacheKey);
    if (cached) {
      this.log('Recommendations cache hit for product:', productId);
      return cached;
    }

    const fetchRecsByIntent = async (intent) => {
      try {
        const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=10&intent=${intent}`);
        if (response.ok) {
          const data = await response.json();
          if (data.products && data.products.length > 0) {
            return data.products;
          }
        }
      } catch (err) {
        console.error(`[Zeniora Promo Engine] Failed to fetch recommendations for intent: ${intent}`, err);
      }
      return [];
    };

    let products = await fetchRecsByIntent('complementary');
    if (products.length < limit) {
      this.log(`Complementary products count (${products.length}) is less than limit (${limit}). Supplementing with intent=related...`);
      const relatedProducts = await fetchRecsByIntent('related');
      
      const combined = [...products];
      relatedProducts.forEach(rp => {
        if (!combined.some(p => p.id === rp.id)) {
          combined.push(rp);
        }
      });
      products = combined;
    }

    if (products.length > 0) {
      const filtered = products.filter(p => {
        if (!p) return false;
        if (p.id === parseInt(productId)) return false;
        if (p.available === false || !p.variants || p.variants.length === 0) return false;
        return true;
      });

      const sliced = filtered.slice(0, limit);
      this.setCache(cacheKey, sliced);
      return sliced;
    }
    return [];
  }

  /**
   * 抓取免邮凑单推荐产品数据
   */
  async fetchShippingUpsells() {
    const shippingConfig = this.config.freeShipping;
    if (!shippingConfig) return [];

    const handle = shippingConfig.recommendations?.collectionHandle || 'all';
    
    try {
      const response = await fetch(`/collections/${handle}/products.json?limit=20`);
      if (response.ok) {
        const data = await response.json();
        const cartHandles = this.cartData ? this.cartData.items.map(item => item.handle) : [];
        const filteredProducts = data.products.filter(prod => !cartHandles.includes(prod.handle));
        
        // 控制每次获取详情的最大并发数，由 10 个限制减少至最多 4 个
        const fullDetails = await Promise.all(
          filteredProducts.slice(0, 4).map(prod => this.fetchProductDetails(prod.handle))
        );
        return fullDetails.filter(prod => prod !== null);
      }
    } catch (err) {
      this.log('Error fetching dynamic collection upsells. Using fallback static products.', err);
    }

    // 兜底静态推荐产品
    const fallbacks = shippingConfig.recommendations?.fallbackProducts || [];
    const cartHandles = this.cartData ? this.cartData.items.map(item => item.handle) : [];
    const filteredFallbacks = fallbacks.filter(prod => !cartHandles.includes(prod.handle));
    return await Promise.all(
      filteredFallbacks.slice(0, 4).map(prod => this.fetchProductDetails(prod.handle))
    );
  }

  /**
   * 辅助方法：通过 Shopify 原生 AJAX 接口抓取单个产品详情并缓存
   */
  async fetchProductDetails(handle) {
    if (this.productsCache[handle]) {
      return this.productsCache[handle];
    }
    const cached = this.getCache('product_' + handle);
    if (cached) {
      this.productsCache[handle] = cached;
      return cached;
    }

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (response.ok) {
        const product = await response.json();
        this.productsCache[handle] = product;
        this.setCache('product_' + handle, product);
        return product;
      }
    } catch (err) {
      console.error(`[Zeniora Promo Engine] Failed to fetch product details: ${handle}`, err);
    }
    return null;
  }

  /**
   * 提供给 UI 层的通用加购 API，支持一键加入多件商品到购物车
   */
  async addItemsToCart(items) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items })
      });

      if (response.ok) {
        const cartJson = await response.json();
        this.log('Items added successfully. Dispatching global update event.');
        
        // 触发 Shopify/Minimog 的购物车刷新
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          detail: { open: true }
        }));
        
        return cartJson;
      } else {
        const errJson = await response.json();
        console.error('[Zeniora Promo Engine] Add to cart failed:', errJson);
        alert(errJson.description || '加购失败，请稍后重试！');
      }
    } catch (err) {
      console.error('[Zeniora Promo Engine] Error executing add to cart:', err);
    }
    return null;
  }
}

// 挂载全局实例
window.ZenioraPromoEngineInstance = new ZenioraPromoEngine();
